import { TierBadge } from '@components/ui/TierBadge';
import { getDependencyState } from '@config/toggleDependencies';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import { MIN_HISTORY_THRESHOLD } from '@parlay-picker/shared/strategyConstants';
import { TIER_DISPLAY_NAMES } from '@parlay-picker/shared/subscriptionTier';
import type { AnalysisConfig } from '@views/picks/constants';
import type { ReactElement } from 'react';
import { Section } from './Section';
import { Toggle } from './Toggle';

interface BettingHistorySectionProps {
  cfg: AnalysisConfig;
  config: AnalysisConfig;
  isExpanded: boolean;
  onToggleExpand: (key: string) => void;
  updateConfig: (section: string, key: string, value: boolean | number | string) => void;
  composedToggleProps: (
    dotPath: string,
    checked: boolean,
    featureLocked?: boolean,
    featureLockedReason?: string,
  ) => { disabled: boolean; disabledReason: string | undefined };
  settledLegCount: number;
  onUpgrade: (feature: GatedFeature) => void;
}

export function BettingHistorySection({
  cfg,
  config,
  isExpanded,
  onToggleExpand,
  updateConfig,
  composedToggleProps,
  settledLegCount,
  onUpgrade,
}: BettingHistorySectionProps): ReactElement {
  const { isLocked, getRequiredTier } = useFeatureAccess();
  const communityUpgradeHint = `Upgrade to ${TIER_DISPLAY_NAMES[getRequiredTier('history_community')]} to use betting history metrics`;

  const historyOptions = [
    {
      value: 'community' as const,
      label: 'AI + Community',
      desc: 'All AI picks and community bets',
      feature: 'history_community' as GatedFeature,
    },
    {
      value: 'personal' as const,
      label: 'My Performance',
      desc: 'Your personal betting outcomes',
      feature: 'history_personal' as GatedFeature,
    },
    {
      value: 'off' as const,
      label: 'Off',
      desc: 'No historical feedback',
      feature: null,
    },
  ] as const;

  const enabledCount = [
    cfg.otherFactors.historySource !== 'off',
    cfg.efficiencyMetrics.bettingValueEfficiency === true,
    cfg.efficiencyMetrics.historicalTrends === true,
  ].filter(Boolean).length;

  return (
    <Section
      title="Betting History"
      sectionKey="bettingHistory"
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      enabledCount={enabledCount}
      totalCount={3}
    >
      {/* History Source selector (master control) */}
      <div className="py-2">
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <span className="text-sm text-gray-200">History Source</span>
            <p className="text-xs text-theme-text-secondary mt-0.5">
              Feed historical outcomes into AI picks
            </p>
            {getDependencyState(config, 'otherFactors.historySource').disabled && (
              <p className="text-xs text-orange-400/80 mt-0.5">
                {getDependencyState(config, 'otherFactors.historySource').disabledReason}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-1 bg-theme-surface rounded-lg p-1">
          {historyOptions.map((opt) => {
            const currentSource = cfg.otherFactors.historySource;
            const isActive = currentSource === opt.value;
            const locked = opt.feature !== null && isLocked(opt.feature);
            const depState = getDependencyState(config, 'otherFactors.historySource');
            const depDisabled = depState.disabled;
            // Pro users still need enough settled legs for history to be useful
            const needsHistory =
              opt.value !== 'off' &&
              !locked &&
              !depDisabled &&
              settledLegCount < MIN_HISTORY_THRESHOLD;
            const disabled = locked || depDisabled || needsHistory;
            const tooltip = depDisabled
              ? depState.disabledReason
              : locked
                ? opt.desc
                : needsHistory
                  ? settledLegCount === 0
                    ? 'No betting history yet'
                    : `Need ${MIN_HISTORY_THRESHOLD}+ settled legs (you have ${settledLegCount})`
                  : opt.desc;
            return (
              <button
                type="button"
                key={opt.value}
                onClick={() => {
                  if (depDisabled && opt.value !== 'off') return;
                  if (locked) {
                    if (opt.feature) onUpgrade(opt.feature);
                    return;
                  }
                  if (needsHistory) return;
                  updateConfig('otherFactors', 'historySource', opt.value);
                }}
                className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition ${
                  isActive && !disabled
                    ? 'bg-purple-600 text-white'
                    : disabled
                      ? 'text-theme-text-tertiary hover:bg-theme-surface-hover opacity-50'
                      : 'text-theme-text-secondary hover:text-gray-200 hover:bg-theme-surface-hover'
                }`}
                title={tooltip ?? undefined}
              >
                <span className="flex items-center justify-center gap-1">
                  {opt.label}
                  {locked && opt.feature && <TierBadge feature={opt.feature} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-theme-border/50 my-2" />

      {/* Efficiency metrics (depend on historySource being ON) */}
      <Toggle
        label={
          <span className="flex items-center gap-1.5">
            Betting Value Efficiency
            {isLocked('history_community') && <TierBadge feature="history_community" />}
          </span>
        }
        description="Analyze margin vs line (how much players beat/miss props)"
        checked={cfg.efficiencyMetrics.bettingValueEfficiency}
        onChange={(v) => updateConfig('efficiencyMetrics', 'bettingValueEfficiency', v)}
        {...composedToggleProps(
          'efficiencyMetrics.bettingValueEfficiency',
          cfg.efficiencyMetrics.bettingValueEfficiency,
          isLocked('history_community'),
          communityUpgradeHint,
        )}
      />
      <Toggle
        label={
          <span className="flex items-center gap-1.5">
            Historical Trends
            {isLocked('history_community') && <TierBadge feature="history_community" />}
          </span>
        }
        description="Track improving/declining efficiency over time"
        checked={cfg.efficiencyMetrics.historicalTrends}
        onChange={(v) => updateConfig('efficiencyMetrics', 'historicalTrends', v)}
        {...composedToggleProps(
          'efficiencyMetrics.historicalTrends',
          cfg.efficiencyMetrics.historicalTrends,
          isLocked('history_community'),
          communityUpgradeHint,
        )}
      />
    </Section>
  );
}
